import React, { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { type RootState } from '../../store/store';
import { type MemberCountry } from '../../apis/member/member.types';
import { selectCustomerFromStore } from '../../store/selectors/customer';
import { getCountryCode } from '../../business/customerHelper';
import { useGetDDFSCountriesQuery } from '../../apis/dictionary/queries';

interface CountriesProps {
  countries: MemberCountry[];
  customerCountry: MemberCountry;
  setCustomerCountry: (customerCountry: MemberCountry) => void;
  setIsEnabled: (isEnabled: boolean) => void;
}

const CountryProviderContext = createContext<CountriesProps>({
  countries: [],
  customerCountry: { codeISO2: undefined, codeISO3: '', name: '', phoneAreaCode: '' },
  setCustomerCountry: () => {},
  setIsEnabled: () => {},
});

const defaultCountry: MemberCountry = { codeISO2: undefined, codeISO3: '', name: '', phoneAreaCode: '' };

interface CountryProviderProps {
  readonly children: ReactNode;
}

export const CountryProvider = ({ children }: CountryProviderProps) => {
  const customer = useSelector(selectCustomerFromStore);
  const [isEnabled, setIsEnabled] = useState(false);
  const [customerCountry, setCustomerCountry] = useState<MemberCountry>(defaultCountry);
  const siteCountryCode = useSelector((state: RootState) => state.userInfo.selectedSite.address.countryCode);
  const countriesQuery = useGetDDFSCountriesQuery(isEnabled);

  const countryCode = customer?.countryCode ?? siteCountryCode;

  useEffect(() => {
    if (countriesQuery.data?.length) {
      const currentCustomerCountry = countriesQuery.data.find(country => getCountryCode(country)?.toUpperCase() === countryCode.toUpperCase());
      setCustomerCountry(currentCustomerCountry || countriesQuery.data[0]);
    }
  }, [countriesQuery.data, countryCode]);

  const contextValue: CountriesProps = useMemo(
    () => ({
      countries: countriesQuery.data ?? [],
      customerCountry,
      setCustomerCountry,
      setIsEnabled,
    }),
    [customerCountry, countriesQuery.data]
  );

  return <CountryProviderContext.Provider value={contextValue}>{children}</CountryProviderContext.Provider>;
};

export const useCountryContext = () => {
  const countryContext = useContext(CountryProviderContext);

  if (!countryContext) {
    throw new Error('useCountryContext must be used inside the CountryProviderContext');
  }

  return countryContext;
};
